import {
  Application,
  Merchant,
  MerchantSchema,
  PayeeDetailsPerCurrency,
} from "@/types/merchant";
import { queryOptions } from "@tanstack/react-query";
import { env } from "@/env";
import { redirectToSignIn } from "./auth";

export function getMerchantQuery({
  accessToken,
  merchantId,
}: {
  accessToken: string;
  merchantId: string;
}) {
  const merchantQuery = queryOptions({
    queryKey: ["merchant", merchantId],
    // Always keep merchant data until the user reloads the page
    // At some point move this out of here and use a global state store thats not router context
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: "always",
    queryFn: async () => {
      const response = await fetch(
        `${env.VITE_VOLUME_URL}/api/merchants/${merchantId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.status === 401 || response.status === 403) {
        throw redirectToSignIn(false);
      }

      if (!response.ok) {
        throw new Error("Failed to fetch merchant details");
      }
      const json = await response.json();
      const merchantData = MerchantSchema.parse(json);

      const processedApplications = merchantData.applications.map(
        (application) => {
          const payeeDetails = application.payeeDetailsPerCurrency || [];
          const hasEUR = payeeDetails.some((c) => c.forCurrency === "EUR");
          const hasGBP = payeeDetails.some((c) => c.forCurrency === "GBP");

          return {
            hasEUR,
            hasGBP,
            hasEURForDefaultVirtual: payeeDetails.some(
              (c) =>
                c.forCurrency === "EUR" &&
                c.defaultForCurrency &&
                c.isVirtualAccount,
            ),
            hasGBPForDefaultVirtual: payeeDetails.some(
              (c) =>
                c.forCurrency === "GBP" &&
                c.defaultForCurrency &&
                c.isVirtualAccount,
            ),
            shouldSendEmailForExternalPayinPayment:
              application.sendEmailForExternalPayinPayment ?? false,
            shouldSendWebhookForExternalPayinPayment:
              application.sendWebhookForExternalPayinPayment ?? false,
            vaProviders:
              application.payeeDetailsPerCurrency?.map(
                (p) => p.vaProviderType,
              ) ?? [],
            ...application,
          };
        },
      );

      return {
        merchant: merchantData,
        applications: processedApplications,
      };
    },
  });
  return merchantQuery;
}

export interface MerchantQuery {
  merchant: Merchant;
  applications: ({
    hasEUR: boolean;
    hasGBP: boolean;
    hasEURForDefaultVirtual: boolean;
    hasGBPForDefaultVirtual: boolean;
    shouldSendEmailForExternalPayinPayment: boolean;
    shouldSendWebhookForExternalPayinPayment: boolean;
    vaProviders: PayeeDetailsPerCurrency["vaProviderType"][];
  } & Application)[];
}
